import React, { Fragment } from 'react'

import { Stack, Typography, Box, Alert, Button, alpha, useTheme } from '@mui/material'
import { CheckCircle } from '@mui/icons-material'
import { PaymentCreditCard } from '@/types/Payment'

export interface CardSelectorProps {
  cards: PaymentCreditCard[]
  selectedCardId: number | null
  onSelect: (cardId: number) => void
  onReregister: (cardId: number) => void
}

export const CardSelector: React.FC<CardSelectorProps> = ({ cards, selectedCardId, onSelect, onReregister }) => {
  const theme = useTheme()

  return (
    <Stack spacing={1.5}>
      {cards.map((card) => {
        const isSelected = selectedCardId === card.id
        const requiresReregistration = isSelected && card.requires_reregistration

        return (
          <Fragment key={card.id}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              border="1px solid"
              borderColor={isSelected ? 'primary.main' : 'divider'}
              bgcolor={isSelected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper'}
              padding={2}
              borderRadius={2}
              onClick={() => onSelect(card.id)}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'action.hover',
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  component="img"
                  src={`/assets/png/card-brands/${card.brand.toLowerCase()}.png`}
                  alt={card.brand}
                  height={26}
                  sx={{ objectFit: 'contain' }}
                />
                <Stack direction="column">
                  <Typography variant="body2" fontWeight="medium">
                    {card.card_mask.replace(/(.{4})/g, '$1 ')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Vencimento: {card.expiration_month}/{card.expiration_year}
                  </Typography>
                </Stack>
              </Stack>
              {isSelected && (
                <CheckCircle
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    color: 'primary.main',
                    fontSize: 20,
                  }}
                />
              )}
            </Stack>
            {requiresReregistration && (
              <Alert
                severity="warning"
                action={
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    fullWidth
                    onClick={() => onReregister(card.id)}
                  >
                    Recadastrar cartão
                  </Button>
                }
                sx={{
                  flexWrap: 'wrap',
                  '& .MuiAlert-message': { flex: 1 },
                  '& .MuiAlert-action': { flexBasis: '100%', m: 0, px: 0, py: 1 },
                }}
              >
                <Typography variant="body2">
                  Para continuar com este cartão, será necessário cadastrá-lo novamente.
                </Typography>
              </Alert>
            )}
          </Fragment>
        )
      })}
    </Stack>
  )
}
